import { useState, useEffect, useRef } from 'react';
import { postRequest } from '../../utils/networking';
import { apiUrls, imageUrl as imgUrl } from '../../utils/url';
import { toast } from 'react-toastify';
import { Calendar, Plus, Loader2, Edit } from 'lucide-react';
import { IndianRupee } from '../../components/CustomIcons';
import Button from '../../components/Button';
import CommonToastContainer from '../../components/CommonToastContainer';
import CreateWebinarPopup from '../../components/CreateWebinarPopup';

function getPath(fullPath) {
  if (!fullPath) return null;
  const normalized = fullPath.replace(/\\/g, "/");
  let path = normalized;
  if (path.startsWith('/')) {
    path = path.substring(1);
  }
  const assetsIndex = path.indexOf("assets/");
  if (assetsIndex !== -1) {
    return path.substring(assetsIndex + 7);
  }

  const asstesIndex = path.indexOf("asstes/");
  if (asstesIndex !== -1) {
    return path.substring(asstesIndex + 7);
  }
  
  if (path.includes("home/")) {
    return path.replace("home/", "");
  }
  return path;
}

const getFullImageUrl = (imagePath) => {
  const FALLBACK_IMAGE = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40"%3E%3Crect width="40" height="40" fill="%23f3f4f6"/%3E%3Ctext x="50%25" y="50%25" font-size="10" text-anchor="middle" dy=".3em" fill="%239ca3af"%3ENo Image%3C/text%3E%3C/svg%3E';
   if (!imagePath) return FALLBACK_IMAGE;
  let extractedPath = getPath(imagePath);
  
  const finalUrl = `${imgUrl}/${extractedPath}`;
  
  console.log('=== IMAGE URL PROCESSING (WebinarList) ===');
  console.log('Original:', imagePath);
  console.log('Extracted path:', extractedPath);
  console.log('Final URL:', finalUrl);
  console.log('===========================================');
  
  return finalUrl;
};

const stripHtml = (html) => {
  if (!html) return '';
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || "";
  } catch (e) {
    return html.replace(/<[^>]*>/g, '');
  }
};

export default function WebinarList() {
    const [webinars, setWebinars] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingWebinar, setEditingWebinar] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [imageErrors, setImageErrors] = useState({});
    const [imagePreview, setImagePreview] = useState('');
    const fileInputRef = useRef(null);
    const [formData, setFormData] = useState({
        webinarname: '',
        webinardescription: '',
        mentorname: '',
        mentorimg: null,
        asondate: '',
        price: '',
        sharedInfo: '' 
    });

    const formatDate = (dateString) => {
        if (!dateString || dateString === 'N/A') return 'N/A';

        const date = new Date(dateString);
        if (isNaN(date)) return 'N/A';

        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const formatDateTimeForInput = (dateString) => {
        if (!dateString || dateString === 'N/A') return '';

        const date = new Date(dateString);
        if (isNaN(date)) return '';

        return date.toISOString().slice(0, 16);
    };

    const formatPrice = (price) => {
        if (!price || price === '0' || price === 'N/A') return 'Free';
        return `₹${parseFloat(price).toFixed(2)}`;
    };

    const handleImageError = (webinarId) => {
        if (!imageErrors[webinarId]) {
            console.log(`Image failed to load for webinar ${webinarId}`);
            setImageErrors(prev => ({
                ...prev,
                [webinarId]: true
            }));
        }
    };

    const fetchCurrentWebinars = async () => {
        setIsLoading(true);
        setError('');

        try {
            const response = await postRequest(apiUrls.getcurrentwebinar);
            console.log('Current webinars response:', response);

            if (response.status == 'S' && response.data) {
                const webinarData = Array.isArray(response.data) ? response.data : [];
                const formattedWebinars = webinarData.map((item, index) => {
                    const mentorImage = item.mentorimg || item.mentorprofileurl || item.mentorProfileImage;
                    
                    let processedImageUrl;
                    if (mentorImage) {
                        processedImageUrl = getFullImageUrl(mentorImage);
                    } else {
                        processedImageUrl = 'https://via.placeholder.com/40?text=No+Image';
                    }
                    
                    return {
                        id: item.id || index + 1,
                        webinarid: item.webinarid || item.id,
                        title: item.webinarname || 'Untitled Webinar',
                        description: item.webinardescription || 'No description available',
                        mentorName: item.mentorname || 'Unknown Mentor',
                        mentorProfileImage: processedImageUrl,
                        webinarOn: item.asondate || new Date().toISOString(),
                        price: item.price || item.webinarprice || '0',
                        sharedInfo: item.sharedinfo || item.sharedinformation || item.shared_info || item.sharedInfo || '',
                        rawData: item
                    };
                });

                setWebinars(formattedWebinars);
            } else {
                const errorMsg = response.message || 'Failed to fetch webinar data';
                setError(errorMsg);
                toast.error(errorMsg);
                setWebinars([]);
            }
        } catch (err) {
            console.error('Error fetching current webinars:', err);
            const errorMsg = 'Unable to load webinars. Please try again later.';
            setError(errorMsg);
            toast.error(errorMsg);
            setWebinars([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Please upload a valid image file (JPEG, PNG, JPG, or GIF)');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            toast.error('Image size should be less than 5MB');
            return;
        }

        setUploadingImage(true);

        try {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
            
            setFormData(prev => ({ ...prev, mentorimg: file }));
            toast.success('Image selected successfully!');
        } catch (err) {
            console.error('Error handling image:', err);
            toast.error('Failed to load image');
        } finally {
            setUploadingImage(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleCreateWebinar = async (e) => {
        e.preventDefault();

        if (!formData.webinarname || !formData.webinardescription || !formData.mentorname || !formData.asondate) {
            toast.error('Please fill in all required fields');
            return;
        }

        setIsSubmitting(true);
        
        const cleanSharedInfo = (html) => {
            if (!html) return '';
            return html.replace(/^<p>/, '').replace(/<\/p>$/, '');
        };

        try {
            const submitFormData = new FormData();
            submitFormData.append('webinarname', formData.webinarname);
            submitFormData.append('webinardescription', formData.webinardescription);
            submitFormData.append('mentorname', formData.mentorname);
            submitFormData.append('asondate', formData.asondate);
            submitFormData.append('price', formData.price || '0');
            submitFormData.append('sharedInfo', cleanSharedInfo(formData.sharedInfo));
            submitFormData.append('sharedinfo', cleanSharedInfo(formData.sharedInfo));
            
            if (formData.mentorimg instanceof File) {
                submitFormData.append('mentorimg', formData.mentorimg);
                console.log('Sending file:', formData.mentorimg);
            }

            const response = await postRequest(apiUrls.createwebinar, submitFormData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            
            console.log('Create webinar response:', response);

            if (response.status === 'S') {
                toast.success(response.message || 'Webinar created successfully!');
                setIsCreateModalOpen(false);
                setFormData({
                    webinarname: '',
                    webinardescription: '',
                    mentorname: '',
                    mentorimg: null,
                    asondate: '',
                    price: '',
                    sharedinfo: ''
                });
                setImagePreview('');
                await fetchCurrentWebinars();
            } else {
                const errorMsg = response.message || 'Failed to create webinar';
                toast.error(errorMsg);
            }
        } catch (err) {
            console.error('Error creating webinar:', err);
            toast.error('Unable to create webinar. Please try again later.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEditWebinar = async (e) => {
        e.preventDefault();

        if (!formData.webinarname || !formData.webinardescription || !formData.mentorname || !formData.asondate) {
            toast.error('Please fill in all required fields');
            return;
        }

        setIsSubmitting(true);
        
        const cleanSharedInfo = (html) => {
            if (!html) return '';
            return html.replace(/^<p>/, '').replace(/<\/p>$/, '');
        };

        try {
            const submitFormData = new FormData();
            submitFormData.append('webinarid', editingWebinar.webinarid || editingWebinar.id);
            submitFormData.append('webinarname', formData.webinarname);
            submitFormData.append('webinardescription', formData.webinardescription);
            submitFormData.append('mentorname', formData.mentorname);
            submitFormData.append('asondate', formData.asondate);
            submitFormData.append('price', formData.price || '0');
            submitFormData.append('sharedInfo', cleanSharedInfo(formData.sharedInfo));
            submitFormData.append('sharedinfo', cleanSharedInfo(formData.sharedInfo));
            
            if (formData.mentorimg instanceof File) {
                submitFormData.append('mentorimg', formData.mentorimg);
            }

            console.log('Update request with all fields');

            const response = await postRequest(apiUrls.updatewebinar, submitFormData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            console.log('Update webinar response:', response);

            if (response.status === 'S') {
                toast.success(response.message || 'Webinar updated successfully!');
                setIsEditModalOpen(false);
                setEditingWebinar(null);
                setFormData({
                    webinarname: '',
                    webinardescription: '',
                    mentorname: '',
                    mentorimg: null,
                    asondate: '',
                    price: '',
                    sharedInfo: ''
                });
                setImagePreview('');
                await fetchCurrentWebinars();
            } else {
                const errorMsg = response.message || 'Failed to update webinar';
                toast.error(errorMsg);
            }
        } catch (err) {
            console.error('Error updating webinar:', err);
            toast.error('Unable to update webinar. Please try again later.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const openCreateModal = () => {
        setIsCreateModalOpen(true);
    };

    const closeCreateModal = () => {
        setIsCreateModalOpen(false);
        setFormData({
            webinarname: '',
            webinardescription: '',
            mentorname: '',
            mentorimg: null,
            asondate: '',
            price: '',
            sharedinfo: ''
        });
        setImagePreview('');
    };

    const openEditModal = (webinar) => {
        setEditingWebinar(webinar);
        setFormData({
            webinarname: webinar.title,
            webinardescription: webinar.description,
            mentorname: webinar.mentorName,
            mentorimg: null,
            asondate: formatDateTimeForInput(webinar.webinarOn),
            price: webinar.price || '',
            sharedInfo: webinar.sharedInfo || webinar.sharedinfo || ''
        });
        setImagePreview(webinar.mentorProfileImage);
        setIsEditModalOpen(true);
    };

    const closeEditModal = () => {
        setIsEditModalOpen(false);
        setEditingWebinar(null);
        setFormData({
            webinarname: '',
            webinardescription: '',
            mentorname: '',
            mentorimg: null,
            asondate: '',
            price: '',
            sharedinfo: ''
        });
        setImagePreview('');
    };

    const PriceDisplay = ({ price }) => {
        if (!price || price === '0' || price === 'N/A') {
            return <span className="text-sm font-semibold text-green-600">Free</span>;
        }
        return (
            <div className="flex items-center gap-0.5">
                <IndianRupee className="w-3.5 h-3.5 text-green-600" />
                <span className="text-sm font-semibold text-green-600">{parseFloat(price).toFixed(2)}</span>
            </div>
        );
    };

    const getContent = () => {
        if (isLoading) {
            return (
                <div className="flex items-center justify-center py-16">
                    <div className="text-center">
                        <Loader2 className="inline-block animate-spin h-8 w-8 text-amber-500 mb-4" />
                        <p className="text-gray-600">Loading webinars...</p>
                    </div>
                </div>
            );
        }

        if (error && webinars.length === 0) {
            return (
                <div className="flex flex-col items-center justify-center py-16 px-4">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Failed to load webinars</h3>
                    <p className="text-sm text-gray-500 mb-4">{error}</p>
                    <Button
                        onClick={fetchCurrentWebinars}
                        className="px-4 py-2 bg-amber-500 text-white rounded-lg"
                    >
                        Try Again
                    </Button>
                </div>
            );
        }

        if (webinars.length === 0) {
            return (
                <div className="flex flex-col items-center justify-center py-16 px-4">
                    <Calendar className="w-16 h-16 text-gray-400 mb-4" />
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">No upcoming webinars</h3>
                    <p className="text-sm text-gray-500">Check back later for new webinar sessions</p>
                    <Button
                        onClick={openCreateModal}
                        className="mt-4 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-medium flex items-center gap-2"
                    >
                        <Plus className="w-4 h-4" />
                        Create Your First Webinar
                    </Button>
                </div>
            );
        }

        return (
            <div className="w-full overflow-x-auto">
                <div className="min-w-[1000px]">
                    {/* Header */}
                    <div className="grid grid-cols-[60px_1.5fr_1.2fr_100px_100px_140px_80px] gap-4 px-6 py-4 bg-gradient-to-r from-zinc-100 to-zinc-50 border-b border-zinc-200 text-sm font-semibold text-zinc-600 capitalize">
                        <div className="flex items-center justify-center">S.No</div>
                        <div className="flex items-center">Title</div>
                        <div className="flex items-center">Mentor Name</div>
                        <div className="flex items-center justify-center">Profile</div>
                        <div className="flex items-center justify-center">Price</div>
                        <div className="flex items-center justify-center">Webinar On</div>
                        <div className="flex items-center justify-center">Action</div>
                    </div>

                    {/* Rows */}
                    <div>
                        {webinars?.map((webinar, index) => {
                            const webinarId = webinar.id;
                            const hasImageError = imageErrors[webinarId];
                            const imageUrl = hasImageError 
                                ? 'https://via.placeholder.com/40?text=No+Image'
                                : webinar.mentorProfileImage;

                            return (
                                <div
                                    key={webinar.id}
                                    className="grid grid-cols-[60px_1.5fr_1.2fr_100px_100px_140px_80px] gap-4 py-4 px-6 border-b border-zinc-100 hover:bg-zinc-50 transition-colors"
                                >
                                    <div className="flex items-center justify-center">
                                        <span className="text-sm font-semibold text-zinc-800">
                                            {index + 1}
                                        </span>
                                    </div>

                                    {/* Title Column with Ellipsis */}
                                    <div className="flex items-center min-w-0 w-full">
                                        <span 
                                            className="font-medium text-sm text-zinc-900 block truncate w-full"
                                            title={webinar.title}
                                        >
                                            {webinar.title}
                                        </span>
                                    </div>

                                    <div className="flex items-center min-w-0">
                                        <span 
                                            className="text-sm text-zinc-700 font-medium truncate block w-full" 
                                            title={webinar.mentorName}
                                        >
                                            {webinar.mentorName}
                                        </span>
                                    </div>

                                    <div className="flex items-center justify-center">
                                        <div className="relative flex-shrink-0">
                                            <img
                                                src={imageUrl}
                                                alt={webinar.mentorName}
                                                className="w-8 h-8 rounded-full object-cover border-2 border-gray-200"
                                                onError={() => handleImageError(webinarId)}
                                                loading="lazy"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-center">
                                        <PriceDisplay price={webinar.price} />
                                    </div>


                                    <div className="flex items-center justify-center">
                                        <span className="text-xs text-zinc-700 font-medium whitespace-nowrap">
                                            {formatDate(webinar.webinarOn)}
                                        </span>
                                    </div>

                                    <div className="flex items-center justify-center">
                                        <button
                                            onClick={() => openEditModal(webinar)}
                                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0"
                                            title="Edit Webinar"
                                        >
                                            <Edit className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };

    useEffect(() => {
        fetchCurrentWebinars();
    }, []);

    return (
        <>
            <div className="text-zinc-900 antialiased overflow-hidden">
                <main className="max-w-[1400px] mx-auto flex flex-col py-6 px-4 w-full">
                    <div className="mb-6 flex justify-between">
                        <h1 className="text-lg font-bold text-[#1E1B4B]">Active Webinar</h1>
                        <div>
                            <Button
                                onClick={openCreateModal}
                                className="w-50 px-4 py-2 text-sm font-medium bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:bg-orange-600 hover:-translate-y-0.5 transition-all active:translate-y-0 cursor-pointer"
                            >
                                <Plus className="w-5 h-5" />
                                Create Webinar
                            </Button>
                        </div>
                    </div>

                    <div className="bg-white/70 rounded-xl border border-zinc-100 w-full overflow-hidden">
                        {getContent()}
                    </div>
                </main>
            </div>

            <CreateWebinarPopup
                isOpen={isCreateModalOpen}
                onClose={closeCreateModal}
                onSubmit={handleCreateWebinar}
                formData={formData}
                onInputChange={handleInputChange}
                isSubmitting={isSubmitting}
                uploadingImage={uploadingImage}
                fileInputRef={fileInputRef}
                onImageUpload={handleImageUpload}
                imagePreview={imagePreview}
                setImagePreview={setImagePreview}
            />

            <CreateWebinarPopup
                isOpen={isEditModalOpen}
                onClose={closeEditModal}
                onSubmit={handleEditWebinar}
                formData={formData}
                onInputChange={handleInputChange}
                isSubmitting={isSubmitting}
                uploadingImage={uploadingImage}
                fileInputRef={fileInputRef}
                onImageUpload={handleImageUpload}
                imagePreview={imagePreview}
                setImagePreview={setImagePreview}
                isEdit={true}
            />

            <CommonToastContainer />

            <style>{`
                .html-description-preview p {
                    display: inline;
                }
                .html-description-preview p + p::before {
                    content: " ";
                }
                .html-description-preview ul, .html-description-preview ol {
                    display: inline;
                    padding: 0;
                    margin: 0;
                }
                .html-description-preview li {
                    display: inline;
                }
                .html-description-preview li + li::before {
                    content: ", ";
                }
            `}</style>
        </>
    );
}